/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import { useCart } from '@/data/Content/Cart';
import { useNotifications } from '@/data/Content/Notifications';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { shippingInfoUpdateFetcher } from '@/data/Content/_ShippingInfo';
import { useSettings } from '@/data/Settings';
import { BOPIS } from '@/data/constants/checkout';
import { DATA_KEY_STORE_LOCATOR_STORES } from '@/data/constants/dataKey';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { ORDER_CONFIGS } from '@/data/constants/order';
import {
	DEFAULT_LOCATION,
	GOOGLE_MAP_ZOOM,
	STORE_LIST_RADIUS,
	INIT_CLICKED_STORE_INDEX, 
} from '../constants/storeLocator';
import { useStoreLocatorState } from '@/data/state/useStoreLocatorState';
import { ID, TransactionErrorResponse } from '@/data/types/Basic';
import { LatLng, StoreDetails, StoreLocator } from '@/data/types/Store';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { error as logError } from '@/data/utils/loggerUtil';
import { cartMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/cartMutatorKeyMatcher';
import { processShippingInfoUpdateError } from '@/data/utils/processShippingInfoUpdateError';
import { transactionsStoreLocator } from 'integration/generated/transactions';
import { StorelocatorStorelocator, StorelocatorStorelocatorItem } from 'integration/generated/transactions/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { isEmpty } from 'lodash';
import { GetServerSidePropsContext } from 'next';
import { useCallback, useMemo, useRef, useState } from 'react';
import useSWR, { mutate } from 'swr';

const initialStoreLocator: StoreLocator = {
	storeList: [],
	center: DEFAULT_LOCATION,
	noSearch: true,
};
const DATA_KEY_LOCATION = 'STORE_LOCATOR_STORES_LOCATION';

export const dataMap = (data: StorelocatorStorelocator): StoreDetails[] => {
	const physicalStores =
		data?.PhysicalStore?.map(
			(s: StorelocatorStorelocatorItem) =>
				({
					id: s.uniqueID,
					storeName: s.Description?.at(0)?.displayStoreName,
					physicalStoreName: s.storeName,
					storeFullAddress: `${s.addressLine?.at(0)} ${s.addressLine?.at(1)} `,
					postalCode: `${s.postalCode}`,
					phone: s.telephone1,
					coordinates: {
						lng: Number(s.longitude?.trim()),
						lat: Number(s.latitude?.trim()),
					} as LatLng,
					distance:s?.distance,
					attributes: s.Attribute,
					city:s.city,
					displayAddress:`${s.addressLine?.at(0)} ${s.addressLine?.at(1)}, ${s.addressLine?.at(2)!=="Migrate Data By ES"?s.addressLine?.at(2):""}`,
				} as StoreDetails)
		) ?? [];
	return physicalStores;
};

export const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (
		storeId: string,
		latitude: string,
		longitude: string,
		query: {
			[key: string]: string | boolean | ID[] | number;
		},
		params: RequestParams
	) => {
		try {
			const data = await transactionsStoreLocator(pub).storeLocatorFindStores(
				storeId,
				latitude,
				longitude,
				query,
				params
			);
			return data;
		} catch (error) {
			logError(context?.req, 'StoreLocator: fetcher: error: %o', error);
		}
	};

export const useStoresByUniqueIds = (storeIds:string[]=[]) => {
	const { settings } = useSettings();
	const { data, isLoading } = useSWR(
		storeIds!==undefined && storeIds?.length > 0
			? [
					{
						storeId: settings?.storeId ?? '',
						query: {
							physicalStoreId:storeIds
						},
					},
					DATA_KEY_LOCATION,
			  ]
			: null,
		async ([props]) =>
			storeLocatorByUniqueIdsFetcher(true)(props.storeId, props.query, {}),
	);
	return {stores : data?dataMap(data):[] , isLoading};
}

export const storeLocatorByUniqueIdsFetcher =
(pub: boolean, context?: GetServerSidePropsContext) =>
async (
	storeId: string,
	query: {
		physicalStoreId: string[];
	},
	params: RequestParams
) => {
	try {
		const data = await transactionsStoreLocator(pub).storeLocatorFindByStoreUniqueIds(
			storeId,
			query,
			params
		);
		return data;
	} catch (error) {
		logError(context?.req, 'storeLocatorFindByCityStreet: fetcher: error: %o', error);
	}
};
	export const useStoreStockByLocation = (latLon:LatLng, options:Record<string,string>) => {
		const { settings } = useSettings();
		const { data, isLoading } = useSWR(
			latLon && latLon?.lat
				? [
						{
							storeId: settings?.storeId ?? '',
							latitude: String(latLon.lat),
							longitude: String(latLon.lng),
							query: {
								radius: options.radius,
								siteLevelStoreSearch: false,
								maxItems:options.maxItems,
								profileName: options?.PROFILE_NAME,
								radiusUOM: options.radiusUOM,
								sortBy: options?.sortBy,
								filterAttributes: options?.FILTER_ATTRIBUTES
							},
						},
						DATA_KEY_LOCATION,
				  ]
				: null,
			async ([props]) =>
				fetcher(true)(props.storeId, props.latitude, props.longitude, props.query, {}),
		);
		return {stores : data?dataMap(data):[], isLoading};
	}

export const useStoreLocator = () => {
	const { notifyError } = useNotifications();
	const { settings } = useSettings();
	const router = useNextRouter();
	const params = useExtraRequestParameters();
	const { storeId, langId } = getClientSideCommon(settings, router);

	const { physicalStoreIdInCart, pickupOrderItems } = useCart();

	const [mapInstance, setMapInstance] = useState<google.maps.Map>({} as google.maps.Map);

	const [locator, setLocator] = useState<StoreLocator>(initialStoreLocator);

	const [currentLocation, setCurrentLocation] = useState<LatLng>(DEFAULT_LOCATION);
	const [showDirection, setShowDirection] = useState<boolean>(false);
	const [destination, setDestination] = useState<LatLng | null>(null);
	const [directionResults, setDirectionResults] = useState<google.maps.DirectionsResult | null>(
		null
	);
	const [noDirectionPath, setNoDirectionPath] = useState<boolean>(false);
	const [clickedIndex, setClickedIndex] = useState<number>(INIT_CLICKED_STORE_INDEX);
	const [expand, setExpand] = useState<boolean>(true);
	const [hidden, setHidden] = useState<boolean>(true);
	const [searchTerm, setSearchTerm] = useState<string>(EMPTY_STRING);
	const [searchLatLng, setSearchLatLng] = useState<LatLng>({} as LatLng);
	const [searchBoxRef, setSearchBoxRef] = useState<any>(null);
	const searchTextFieldRef = useRef<HTMLInputElement>(null);
	const { storeLocator } = useStoreLocatorState();

	const { data } = useSWR(
		searchLatLng?.lat
			? [
					{
						storeId: storeId ?? '',
						latitude: String(searchLatLng.lat),
						longitude: String(searchLatLng.lng),
						query: {
							radius: STORE_LIST_RADIUS,
							siteLevelStoreSearch: false,
							langId, // langId is need to get display for different locale
						},
					},
					DATA_KEY_STORE_LOCATOR_STORES,
			  ]
			: null,
		async ([props]) =>
			fetcher(true)(props.storeId, props.latitude, props.longitude, props.query, {})
	);

	const closeInfoBox = () => {
		if (expand) {
			setExpand(false);
		} else {
			setExpand(true);
		}
	};

	const onListItemClick = (e: any, index: number) => {
		setHidden(false);
		setClickedIndex(index);
		zoomMapCenter(GOOGLE_MAP_ZOOM.ZOOM, locator?.storeList[index]?.coordinates);
		stopDirection();
	};

	const zoomMapCenter = (zoom: number, center: LatLng) => {
		mapInstance.setZoom(zoom);
		mapInstance.setCenter(center);
	};

	const onMarkerClick = (e: google.maps.MapMouseEvent, index: number, store: StoreDetails) => {
		setClickedIndex(index);
		setHidden(false);
		zoomMapCenter(18, store.coordinates);
		stopDirection();
	};

	const getDirection = () => {
		if (!showDirection) {
			setDestination(locator.storeList[clickedIndex].coordinates);
			setShowDirection(true);
		} else {
			stopDirection();
			zoomMapCenter(GOOGLE_MAP_ZOOM.ZOOM, locator.storeList[clickedIndex].coordinates);
		}
	};

	const stopDirection = () => {
		setShowDirection(false);
		setDestination(null);
		setDirectionResults(null);
		setNoDirectionPath(false);
	};

	const directionsCallback = (
		result: google.maps.DirectionsResult | null,
		status: google.maps.DirectionsStatus
	) => {
		if (result !== null && directionResults === null) {
			if (status === 'OK') {
				setDirectionResults(result);
			} else {
				setNoDirectionPath(true);
			}
		}
	};

	const mapOnLoad = (mapInstance: google.maps.Map) => {
		setMapInstance(mapInstance);
		/* if (storeLocator.selectedStore?.storeName) {
			setLocator({
				storeList: [storeLocator.selectedStore],
				center: storeLocator.selectedStore.coordinates,
				noSearch: true,
			});
		} */
	};

	const clearSearchTermAndCloseEverything = () => {
		if (searchTerm) {
			setSearchTerm(EMPTY_STRING);
		}
		if (searchTextFieldRef.current) {
			searchTextFieldRef.current.value = EMPTY_STRING;
		}
		setHidden(true);
		stopDirection();
		if (!isEmpty(mapInstance)) {
			mapInstance.setZoom(GOOGLE_MAP_ZOOM.INIT);
		}
		setClickedIndex(INIT_CLICKED_STORE_INDEX);
	};

	const findNearStore = () => {
		navigator.geolocation.getCurrentPosition(
			async (position: GeolocationPosition) => {
				setSearchLatLng({ lat: position.coords.latitude, lng: position.coords.longitude });
				clearSearchTermAndCloseEverything();
			},
			(err: any) => {
				logError(undefined, 'StoreLocator: findNearStore: error: %o', err);
			}
		);
	};

	const clearSearch = () => {
		clearSearchTermAndCloseEverything();
		setSearchLatLng({} as LatLng);
		if (storeLocator.selectedStore?.storeName) {
			setLocator({
				storeList: [storeLocator.selectedStore],
				center: storeLocator.selectedStore.coordinates,
				noSearch: false,
			});
		} else {
			setLocator(initialStoreLocator);
		}
	};

	const onPlaceChanged = () => {
		const place = searchBoxRef?.getPlace();
		if (place?.geometry) {
			setSearchLatLng({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng() });
			setSearchTerm(searchTextFieldRef?.current?.value ?? EMPTY_STRING);
			setHidden(true);
			stopDirection();
			mapInstance.setZoom(GOOGLE_MAP_ZOOM.INIT);
		}
	};

	const searchBoxOnLoad = (searchBox: google.maps.places.Autocomplete) => {
		setSearchBoxRef(searchBox);
	};

	const mapCenter = useMemo(
		() => (searchTerm !== EMPTY_STRING ? locator.center : currentLocation),
		[searchTerm, locator, currentLocation]
	);

	const setAsMyStore = useCallback(
		(
				store: StoreDetails,
				selectStore: (storeDetails: StoreDetails) => Promise<void>,
				_inOrderContext: boolean
			) =>
			async () => {
				if (
					physicalStoreIdInCart &&
					physicalStoreIdInCart !== store.id &&
					pickupOrderItems &&
					pickupOrderItems.length > 0
				) {
					// only update if it is already pickup, do not set to pickup if the order is not pickup order
					try {
						await shippingInfoUpdateFetcher(
							storeId,
							{ langId },
							{
								addressId: '',
								x_calculateOrder: ORDER_CONFIGS.calculateOrder,
								x_calculationUsage: ORDER_CONFIGS.calculationUsage,
								x_inventoryValidation: ORDER_CONFIGS.inventoryValidation.toString(),
								x_allocate: ORDER_CONFIGS.allocate,
								x_backorder: ORDER_CONFIGS.backOrder,
								x_remerge: ORDER_CONFIGS.remerge,
								x_check: ORDER_CONFIGS.check,
								orderId: '.',
								orderItem: pickupOrderItems.map((item) => ({
									orderItemId: item.orderItemId,
									physicalStoreId: store.id,
								})),
							} as any,
							params
						);
						await mutate(cartMutatorKeyMatcher(EMPTY_STRING), undefined);
						return selectStore(store);
					} catch (error) {
						notifyError(processShippingInfoUpdateError(error as TransactionErrorResponse, BOPIS));
					}
				} else {
					return selectStore(store);
				}
			},
		[langId, notifyError, params, physicalStoreIdInCart, pickupOrderItems, storeId]
	);

	return {
		data,
		locator,
		setLocator,
		setCurrentLocation,
		searchLatLng,
		showDirection,
		destination,
		directionResults,
		clickedIndex,
		noDirectionPath,
		expand,
		hidden,
		searchTerm,
		currentLocation,
		searchTextFieldRef,
		mapCenter,
		mapOnLoad,
		getDirection,
		stopDirection,
		directionsCallback,
		onMarkerClick,
		closeInfoBox,
		findNearStore,
		searchBoxOnLoad,
		onPlaceChanged,
		onListItemClick,
		clearSearch,
		setAsMyStore,
	};
};
